import React from 'react';
import { LayoutDashboard, Package, UserRound, Settings } from 'lucide-react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import { iconSize, hollowIconDefaults } from '../../util/iconSettings';
import { LinkTabNavHorizontal } from '../../components';

import css from './UserNav.module.css';

// Icon + label shown inside a nav tab. Lucide icons inherit `currentColor`,
// so they automatically match the tab's text color (incl. the dark skin).
const TabLabel = props => {
  const { Icon, labelId } = props;
  return (
    <span className={css.tabLabel}>
      <Icon
        className={css.tabIcon}
        size={iconSize.menuItem}
        {...hollowIconDefaults}
        aria-hidden="true"
      />
      <FormattedMessage id={labelId} />
    </span>
  );
};

/**
 * A component that renders a navigation bar for a user-specific pages.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.currentPage - The current page (e.g. 'ManageListingsPage')
 * @returns {JSX.Element} User navigation component
 */
const UserNav = props => {
  const { className, rootClassName, currentPage, showManageListingsLink } = props;
  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);

  const manageListingsTabMaybe = showManageListingsLink
    ? [
        {
          text: <TabLabel Icon={Package} labelId="UserNav.yourListings" />,
          selected: currentPage === 'ManageListingsPage',
          linkProps: {
            name: 'ManageListingsPage',
          },
        },
      ]
    : [];

  const tabs = [
    {
      text: <TabLabel Icon={LayoutDashboard} labelId="UserNav.dashboard" />,
      selected: currentPage === 'UserDashboardPage',
      disabled: false,
      linkProps: {
        name: 'UserDashboardPage',
      },
    },
    ...manageListingsTabMaybe,
    {
      text: <TabLabel Icon={UserRound} labelId="UserNav.profileSettings" />,
      selected: currentPage === 'ProfileSettingsPage',
      disabled: false,
      linkProps: {
        name: 'ProfileSettingsPage',
      },
    },
    {
      text: <TabLabel Icon={Settings} labelId="UserNav.accountSettings" />,
      selected: ACCOUNT_SETTINGS_PAGES.includes(currentPage),
      disabled: false,
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
  ];

  return (
    <LinkTabNavHorizontal
      className={classes}
      tabRootClassName={css.tab}
      tabs={tabs}
      skin="dark"
      ariaLabel={intl.formatMessage({ id: 'UserNav.screenreader.userNav' })}
    />
  );
};

export default UserNav;
